import { useSyncExternalStore } from 'react';

import {
  createComment,
  deleteComment,
  listComments,
  type CommentResponse,
} from '@/designs-dev/backend/client';
import { onChange } from '@/designs-dev/backend/eventStream';

// The comments on the page being looked at, shared by the pins on the screen
// (CommentLayer) and the thread list beside it (CommentsPanel), which live in
// different trees: the pins are portalled into the phone.

// What a comment's free-form content holds. A thread's first comment carries
// where on its anchor it was made, as fractions of the anchor's box, so the
// pin sits where the click was even as the component resizes.
//
// `resolved` is only ever on a marker reply: see isMarker.
export type CommentContent = {
  text: string;
  x?: number;
  y?: number;
  resolved?: boolean;
};

export const contentOf = (c: CommentResponse): CommentContent => {
  const { text, x, y, resolved } = c.content as Partial<CommentContent>;
  return {
    text: typeof text === 'string' ? text : '',
    ...(typeof x === 'number' && typeof y === 'number' ? { x, y } : {}),
    ...(typeof resolved === 'boolean' ? { resolved } : {}),
  };
};

const byTime = (a: CommentResponse, b: CommentResponse) =>
  a.createdAt.localeCompare(b.createdAt);

// Resolving. designs-backend can't change a comment once it's posted, so a
// thread is resolved, or reopened, by a reply that carries only that:
// { resolved: true } or { resolved: false }. The latest one wins, and they
// never show as replies. Swap for a real field once the backend has one.
export const isMarker = (c: CommentResponse) =>
  c.parentId !== null && contentOf(c).resolved !== undefined;

export const threadsOf = (comments: CommentResponse[]) =>
  comments.filter((c) => !c.parentId).sort(byTime);

export const repliesTo = (comments: CommentResponse[], id: string) =>
  comments.filter((c) => c.parentId === id && !isMarker(c)).sort(byTime);

export const isResolved = (comments: CommentResponse[], id: string) =>
  comments
    .filter((c) => c.parentId === id && isMarker(c))
    .sort(byTime)
    .map((c) => contentOf(c).resolved)
    .at(-1) ?? false;

// A comment being written, before it is posted.
export type Draft = { anchor: string; x: number; y: number };

type State = {
  page: string | null;
  comments: CommentResponse[];
  loading: boolean;
  error: string | null;
  // The thread open in the panel and ringed on the screen.
  selected: string | null;
  draft: Draft | null;
  // Whether the list of every thread is showing. Its own toggle, so comment
  // mode doesn't take room from the design until asked to.
  listOpen: boolean;
  // When I last looked at each thread, as its latest comment's time, by thread
  // id. Kept in this browser: what someone has read is theirs alone.
  seen: Record<string, string>;
};

const SEEN_KEY = 'loft.comments.seen';

const loadSeen = (): Record<string, string> => {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}');
    return raw && typeof raw === 'object'
      ? (raw as Record<string, string>)
      : {};
  } catch {
    // Storage blocked (a private window) or a value that won't parse: start
    // with nothing read, which only means everything shows as new.
    return {};
  }
};

let state: State = {
  page: null,
  comments: [],
  loading: false,
  error: null,
  selected: null,
  draft: null,
  listOpen: false,
  seen: loadSeen(),
};
const listeners = new Set<() => void>();

const set = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

// For event handlers that need the latest state without re-subscribing.
export const commentsSnapshot = () => state;

export const useComments = () =>
  useSyncExternalStore(subscribe, commentsSnapshot);

// Whether someone else has said something in a thread since I last opened it.
// My own comments never make a thread new to me.
export const isUnread = (s: State, thread: CommentResponse, me?: string) => {
  const seen = s.seen[thread.id];
  return [thread, ...repliesTo(s.comments, thread.id)].some(
    (c) => c.author.id !== me && (!seen || c.createdAt > seen),
  );
};

// The open threads, which are what the bar's badge counts. Replies don't add
// to it, so a busy thread is still one thing to look at; resolved ones are
// done with.
export const openThreads = (s: State) =>
  threadsOf(s.comments).filter((t) => !isResolved(s.comments, t.id));

export const hasUnread = (s: State, me?: string) =>
  openThreads(s).some((t) => isUnread(s, t, me));

// Marks a thread read up to its latest comment. The comment's own time rather
// than the clock's, so a laptop's clock being off can't hide or invent one.
export const markSeen = (threadId: string) => {
  const latest = [
    ...state.comments.filter((c) => c.id === threadId),
    ...repliesTo(state.comments, threadId),
  ]
    .map((c) => c.createdAt)
    .sort()
    .at(-1);
  if (!latest || state.seen[threadId] === latest) return;
  const seen = { ...state.seen, [threadId]: latest };
  set({ seen });
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Storage blocked: read state lasts until the page reloads, which is all
    // a private window would keep anyway.
  }
};

export const setResolved = (thread: CommentResponse, resolved: boolean) =>
  addComment(thread.anchor, { text: '', resolved }, thread.id);

export const selectThread = (id: string | null) =>
  set({ selected: id, draft: null });
export const startDraft = (draft: Draft | null) =>
  set({ draft, selected: null });
export const setListOpen = (listOpen: boolean) => set({ listOpen });

const describe = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const load = async (page: string) => {
  try {
    const { comments } = await listComments({ page, limit: 500 });
    // A slow answer for a page we've since left is dropped.
    if (state.page === page) set({ comments, loading: false, error: null });
  } catch (error) {
    console.error('loading comments failed', error);
    if (state.page === page) set({ loading: false, error: describe(error) });
  }
};

// Loads a page's comments and keeps them current from the live stream. Any
// change to a comment on this page, from anyone, refetches the page: simpler
// than patching, and a page holds tens of comments, not thousands.
export const watchPage = (page: string) => {
  set({
    page,
    comments: [],
    loading: true,
    error: null,
    selected: null,
    draft: null,
  });
  void load(page);
  const unsubscribe = onChange('comment', (change) => {
    if (change.scope === page) void load(page);
  });
  return () => {
    unsubscribe();
    if (state.page === page) set({ page: null, comments: [] });
  };
};

export const addComment = async (
  anchor: string,
  content: CommentContent,
  parentId: string | null = null,
) => {
  const page = state.page;
  if (!page) return;
  const created = await createComment({ page, anchor, content, parentId });
  // The stream will say so too, but showing it now doesn't wait on the socket.
  if (state.page === page)
    set({
      comments: [...state.comments.filter((c) => c.id !== created.id), created],
    });
  return created;
};

export const removeComment = async (id: string) => {
  await deleteComment(id);
  // Deleting a thread's first comment takes its replies with it.
  set({
    comments: state.comments.filter((c) => c.id !== id && c.parentId !== id),
    selected: state.selected === id ? null : state.selected,
  });
};
